import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Button, Badge,
  Spinner, Alert, Modal
} from 'react-bootstrap';
import axios from 'axios';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const PublicAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const API_BASE_URL = BASE_URL;

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Try multiple endpoints to ensure we get data
      const endpoints = [
        `${API_BASE_URL}/announcements.php`, // Default endpoint
        `${API_BASE_URL}/announcements.php?admin=true`, // Admin endpoint (shows all)
      ];
      
      let announcementsData = [];
      
      for (const endpoint of endpoints) {
        try {
          console.log(`Fetching from: ${endpoint}`);
          const response = await axios.get(endpoint, { timeout: 5000 });
          
          if (response.data.success && Array.isArray(response.data.data)) {
            announcementsData = response.data.data;
            console.log(`Found ${announcementsData.length} announcements from ${endpoint}`);
            break; // Stop trying once we get data
          }
        } catch (endpointError) {
          console.log(`Failed to fetch from ${endpoint}:`, endpointError.message);
          continue; // Try next endpoint
        }
      }
      
      // If we still don't have data, try direct fetch
      if (announcementsData.length === 0) {
        try {
          const directResponse = await fetch(`${API_BASE_URL}/announcements.php`);
          const directData = await directResponse.json();
          
          if (directData.success && Array.isArray(directData.data)) {
            announcementsData = directData.data;
            console.log(`Found ${announcementsData.length} announcements from direct fetch`);
          }
        } catch (directError) {
          console.log('Direct fetch also failed:', directError.message);
        }
      }
      
      if (announcementsData.length > 0) {
        // Filter only active announcements (in case admin endpoint was used)
        const activeAnnouncements = announcementsData.filter(ann => 
          ann.is_active === true || ann.is_active === 1 || ann.is_active === "1"
        );
        setAnnouncements(activeAnnouncements);
        console.log(`Displaying ${activeAnnouncements.length} active announcements`);
        
        // Check for popup announcements
        const popupAnnouncements = activeAnnouncements.filter(ann => 
          ann.show_popup === true || ann.show_popup === 1 || ann.show_popup === "1"
        );
        console.log(`${popupAnnouncements.length} announcements are marked as popup`);
      } else {
        setError('No announcements found. Please check if announcements are active in the admin panel.');
      }
      
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Error loading announcements. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const getColorClass = (color) => {
    switch (color) {
      case 'danger': return 'danger';
      case 'warning': return 'warning';
      case 'info': return 'info';
      case 'success': return 'success';
      case 'primary': return 'primary';
      case 'secondary': return 'secondary';
      case 'dark': return 'dark';
      default: return 'danger';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getAttachmentUrl = (attachmentPath) => {
    if (!attachmentPath) return null;
    return `${UPLOAD_BASE_URL}${attachmentPath}`;
  };

  const handleViewDetail = (announcement) => {
    setSelectedAnnouncement(announcement);
    setShowDetail(true);
  };

  const getFileIcon = (filename) => {
    if (!filename) return 'fas fa-file';
    const ext = filename.split('.').pop().toLowerCase();
    switch (ext) {
      case 'pdf': return 'fas fa-file-pdf text-danger';
      case 'doc':
      case 'docx': return 'fas fa-file-word text-primary';
      case 'txt': return 'fas fa-file-alt text-secondary';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return 'fas fa-file-image text-success';
      default: return 'fas fa-file';
    }
  };

  if (loading) {
    return (
      <Container className="text-center my-5 py-5">
        <Spinner animation="border" variant="primary" />
        <h4 className="mt-3">Loading Announcements...</h4>
        <p className="text-muted small">Please wait while we fetch the latest announcements</p>
      </Container>
    );
  }

  return (
    <>
      <div className="bg-light py-5 mb-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} className="text-center">
              <h1 className="display-5 fw-bold text-primary mb-3">
                <i className="fas fa-bullhorn me-3"></i>
                Announcements & Notices
              </h1>
              <p className="lead text-muted mb-4">
                Stay updated with our latest news and important information
              </p>
              <Badge bg="primary" className="px-4 py-2 fs-6">
                <i className="fas fa-bell me-2"></i>
                {announcements.length} Announcement{announcements.length !== 1 ? 's' : ''}
              </Badge>
              
              {error && (
                <Alert variant="warning" className="mt-4">
                  <Alert.Heading>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Information
                  </Alert.Heading>
                  <p>{error}</p>
                  <hr />
                  <div className="d-flex justify-content-between">
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={fetchAnnouncements}
                    >
                      <i className="fas fa-redo me-1"></i>
                      Refresh
                    </Button>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => console.log('Announcements data:', announcements)}
                    >
                      <i className="fas fa-bug me-1"></i>
                      Debug
                    </Button>
                  </div>
                </Alert>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="mb-5">
        {announcements.length > 0 ? (
          <>
            <Row className="g-4">
              {announcements.map((announcement) => (
                <Col key={announcement.id} lg={6}>
                  <Card 
                    className="h-100 shadow-sm border-0 announcement-card"
                    style={{ 
                      borderLeft: `4px solid var(--bs-${getColorClass(announcement.icon_color)})`,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-start mb-3">
                        <div className="flex-shrink-0">
                          <div className={`rounded-circle bg-${getColorClass(announcement.icon_color)} bg-opacity-10 
                            p-3 d-flex align-items-center justify-content-center`}
                            style={{ width: '60px', height: '60px' }}>
                            <i className={`${announcement.icon || 'fas fa-bullhorn'} 
                              text-${getColorClass(announcement.icon_color)} fs-4`}></i>
                          </div>
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <h4 className="mb-1">{announcement.title}</h4>
                          <div className="d-flex flex-wrap gap-2 mb-2">
                            <Badge bg="light" text="dark" className="px-2 py-1">
                              <i className="fas fa-calendar-alt me-1"></i>
                              {formatDate(announcement.date)}
                            </Badge>
                            {announcement.show_popup && (
                              <Badge bg="info" className="px-2 py-1">
                                <i className="fas fa-external-link-alt me-1"></i>
                                Popup
                              </Badge>
                            )}
                            {announcement.attachment && (
                              <Badge bg="secondary" className="px-2 py-1">
                                <i className="fas fa-paperclip me-1"></i>
                                Attachment
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mb-4">
                        <div 
                          className="text-muted"
                          dangerouslySetInnerHTML={{ 
                            __html: announcement.content.length > 150 
                              ? `${announcement.content.substring(0, 150)}...` 
                              : announcement.content 
                          }}
                        />
                      </div>

                      <div className="d-flex justify-content-between align-items-center border-top pt-3">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleViewDetail(announcement)}
                        >
                          <i className="fas fa-eye me-1"></i>
                          Read More
                        </Button>
                        
                        {announcement.attachment && (
                          <a 
                            href={getAttachmentUrl(announcement.attachment)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-secondary btn-sm"
                          >
                            <i className="fas fa-download me-1"></i>
                            Download
                          </a>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
            
            {/* Stats Card */}
            <Card className="mt-5 shadow-sm border-0 bg-light">
              <Card.Body className="p-4">
                <Row className="text-center">
                  <Col md={3}>
                    <h3 className="text-primary mb-0">{announcements.length}</h3>
                    <small className="text-muted">Total Announcements</small>
                  </Col>
                  <Col md={3}>
                    <h3 className="text-success mb-0">
                      {announcements.filter(a => a.show_popup).length}
                    </h3>
                    <small className="text-muted">Popup Announcements</small>
                  </Col>
                  <Col md={3}>
                    <h3 className="text-info mb-0">
                      {announcements.filter(a => a.attachment).length}
                    </h3>
                    <small className="text-muted">With Attachments</small>
                  </Col>
                  <Col md={3}>
                    <h3 className="text-warning mb-0">
                      {new Set(announcements.map(a => new Date(a.date).getFullYear())).size}
                    </h3>
                    <small className="text-muted">Years Covered</small>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </>
        ) : (
          <div className="text-center py-5">
            <i className="fas fa-bullhorn fa-3x text-muted mb-3"></i>
            <h4 className="text-muted">No announcements available</h4>
            <p className="text-muted mb-4">Check back later for updates or contact administration</p>
            <Button 
              variant="outline-primary" 
              onClick={fetchAnnouncements}
              className="me-2"
            >
              <i className="fas fa-redo me-1"></i>
              Refresh
            </Button>
            <Button 
              variant="outline-secondary"
              onClick={() => window.location.href = '/'}
            >
              <i className="fas fa-home me-1"></i>
              Go Home
            </Button>
          </div>
        )}
      </Container>

      {/* Detail Modal */}
      <Modal show={showDetail} onHide={() => setShowDetail(false)} size="lg" centered>
        {selectedAnnouncement && (
          <>
            <Modal.Header 
              closeButton
              className={`border-bottom border-${getColorClass(selectedAnnouncement.icon_color)}`}
            >
              <Modal.Title>
                <div className="d-flex align-items-center">
                  <i className={`${selectedAnnouncement.icon} 
                    text-${getColorClass(selectedAnnouncement.icon_color)} me-2 fs-4`}></i>
                  <span>{selectedAnnouncement.title}</span>
                </div>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="mb-4">
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <Badge bg="light" text="dark" className="px-3 py-2">
                    <i className="fas fa-calendar-alt me-1"></i>
                    Posted: {formatDate(selectedAnnouncement.date)}
                  </Badge>
                  <Badge bg="light" text="dark" className="px-3 py-2">
                    <i className="fas fa-clock me-1"></i>
                    Created: {formatDate(selectedAnnouncement.created_at)}
                  </Badge>
                  {selectedAnnouncement.start_date && (
                    <Badge bg="info" className="px-3 py-2">
                      <i className="fas fa-play me-1"></i>
                      Started: {formatDate(selectedAnnouncement.start_date)}
                    </Badge>
                  )}
                  {selectedAnnouncement.end_date && (
                    <Badge bg="warning" className="px-3 py-2">
                      <i className="fas fa-stop me-1"></i>
                      Ends: {formatDate(selectedAnnouncement.end_date)}
                    </Badge>
                  )}
                  {selectedAnnouncement.show_popup && (
                    <Badge bg="success" className="px-3 py-2">
                      <i className="fas fa-external-link-alt me-1"></i>
                      Shows as Popup
                    </Badge>
                  )}
                  {selectedAnnouncement.priority > 0 && (
                    <Badge bg="primary" className="px-3 py-2">
                      <i className="fas fa-star me-1"></i>
                      Priority: {selectedAnnouncement.priority}
                    </Badge>
                  )}
                </div>
                
                <div className="announcement-content border-top pt-3">
                  <div dangerouslySetInnerHTML={{ __html: selectedAnnouncement.content }} />
                </div>
                
                {selectedAnnouncement.attachment && (
                  <div className="mt-4 border-top pt-3">
                    <h5>
                      <i className="fas fa-paperclip me-2"></i>
                      Attachment
                    </h5>
                    <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded">
                      <div className="d-flex align-items-center">
                        <i className={getFileIcon(selectedAnnouncement.attachment)}></i>
                        <span className="ms-2">{selectedAnnouncement.attachment.split('/').pop()}</span>
                      </div>
                      <a 
                        href={getAttachmentUrl(selectedAnnouncement.attachment)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-sm"
                      >
                        <i className="fas fa-download me-1"></i>
                        Download
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowDetail(false)}>
                Close
              </Button>
              <Button 
                variant="outline-primary"
                onClick={() => {
                  setShowDetail(false);
                  setTimeout(() => {
                    window.scrollTo(0, 0);
                  }, 100);
                }}
              >
                <i className="fas fa-arrow-up me-2"></i>
                Back to Top
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      {/* Custom Styles */}
      <style jsx="true">{`
        .announcement-card:hover {
          box-shadow: 0 10px 30px rgba(0,0,0,0.1) !important;
        }
        
        .announcement-content {
          line-height: 1.8;
        }
        
        .announcement-content h1,
        .announcement-content h2,
        .announcement-content h3,
        .announcement-content h4 {
          margin-top: 1.5rem;
          margin-bottom: 1rem;
          color: #333;
        }
        
        .announcement-content p {
          margin-bottom: 1.2rem;
          color: #555;
        }
        
        .announcement-content ul,
        .announcement-content ol {
          padding-left: 1.8rem;
          margin-bottom: 1.2rem;
        }
        
        .announcement-content li {
          margin-bottom: 0.5rem;
        }
        
        .announcement-content img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 1rem 0;
        }
        
        .announcement-content blockquote {
          border-left: 4px solid #0d6efd;
          padding-left: 1.5rem;
          margin: 1.5rem 0;
          font-style: italic;
          color: #666;
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .display-5 {
            font-size: 2.5rem;
          }
          
          .lead {
            font-size: 1.1rem;
          }
        }
        
        @media (max-width: 576px) {
          .display-5 {
            font-size: 2rem;
          }
          
          .card {
            margin-bottom: 1.5rem;
          }
        }
        
        /* Animation for cards */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .announcement-card {
          animation: fadeInUp 0.5s ease-out;
        }
      `}</style>
    </>
  );
};

export default PublicAnnouncements;